import React, { useState } from "react";
import "./details.scss";
import "../../../sass/variables.module.scss";
import SlideOutSidebar from "../../../ui-components/slide-out-sidebar/slideOutSidebar";
import EditAddress from "./editDetails/editAddress";
import { Divider } from "features/terminal-details/common/divider";
import EditRegion from "./editDetails/editRegion";
import { TerminalState } from "../../../slices/terminal-summary/TerminalDetailsState";
import { useSelector } from "react-redux";
import { terminalDetailsSelector } from "../../../slices/terminal-summary/terminalDetailsSlice";
import EditContact from "./editDetails/editContact";
import { ReactComponent as EditIcon } from "images/edit_solid.svg";
import Loading from "ui-components/loading/loading";
import _ from "lodash";
import { UserUtils } from "app/data/user/userUtils";
import UserState from "slices/user/UserState";
import { userSelector } from "slices/user/userSlice";
import usaStates from "app/data/common/usaStates";
import { countryOptions } from "app/data/terminal-details/models";

interface DetailsWidgetProps { }

interface DetailsSectionData {
    primaryDetails?: { label: string, value: string | boolean | JSX.Element }[];
    secondaryDetails?: { label: string, value: string | boolean | JSX.Element }[];
    tertiaryDetails?: { label: string, value: string | boolean | JSX.Element }[];
}

const DetailsHeader = (props: { title: string, handleEdit?: () => void }): React.ReactElement => {
    const { title, handleEdit } = props;
    const userState: UserState = useSelector(userSelector);
    
    return (
        <div className="box-header border-bottom">
            <div>{title}</div>
            {UserUtils.isXGSAdministrator(userState.profile) && handleEdit && <div className="edit" onClick={handleEdit} > <span>
                <EditIcon width={15} height={15} style={{ color: "#2F80ED" }} />
            </span>Edit</div>}
        </div>
    )
}

const DetailsRow = (props: { title: string | null, info?: string, details: { label: string, value: string | boolean | JSX.Element }[] }): React.ReactElement => {
    const { title, info, details } = props;

    return (
        <div className="details-row">
            {/* title */}
            {title && <div className="details-row-header">{title} {info && <div className="info">{info}</div>}</div>}

            {/* content */}
            {details?.length > 0 && (
                <div className="details-row-content">
                    {details.map(({ label, value }, index) => (
                        <div className="details-row-content__item" key={index}>
                            <div className="label">{label}</div>
                            <div className="value">{_.isEmpty(value) ? "N/A" : value}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

const BoxSection = (props: {
    title: string,
    details?: DetailsSectionData,
    secondaryInfo?: string,
    sectionTitles?: {
        secondaryTitle?: string,
        tertiaryTitle?: string
    },
    editComponent?: ({ onClose }: { onClose: () => void }) => JSX.Element,
}): React.ReactElement => {
    const { title, details = {}, sectionTitles = {}, editComponent, secondaryInfo } = props;
    const {
        primaryDetails = [],
        secondaryDetails = [],
        tertiaryDetails = []
    } = details;
    const {
        secondaryTitle = "",
        tertiaryTitle = ""
    } = sectionTitles;

    const [isEditing, setIsEditing] = useState<boolean>(false);


    const handleClose = () => {
        setIsEditing(false);
    };
    const handleOpen = () => {
        setIsEditing(true);
    };

    return (
        <>
            <SlideOutSidebar
                show={isEditing}
                onClose={handleClose}
                header={`Edit ${title}`}
                size="small"
            >
                {editComponent && editComponent({ onClose: handleClose })}
            </SlideOutSidebar>
            <div className="box">
                <DetailsHeader title={title} handleEdit={handleOpen} />

                {primaryDetails.length > 0 && (
                    <>
                        <DetailsRow details={primaryDetails} title={null} />
                        <Divider />
                    </>
                )}
                <>
                    <DetailsRow title={secondaryTitle} info={secondaryInfo} details={secondaryDetails} />
                    <Divider />
                </>
                {tertiaryDetails.length > 0 && (
                    <>
                        <DetailsRow title={tertiaryTitle} details={tertiaryDetails} />
                        <Divider />
                    </>
                )}
            </div>
        </>
    )
}

const DetailsWidget: React.FC<DetailsWidgetProps> = () => {
    const terminalState: TerminalState = useSelector(terminalDetailsSelector);

    const sections = [
        {
            title: "Address Information",
            secondaryInfo: terminalState.data?.address?.isMailingAddressSame ? 'Same as physical address' : '',
            details: {
                primaryDetails: [{ label: 'Physical Address', value: terminalState.data?.address?.physicalAddress || (terminalState.isLoading && <Loading isLoading={true} />) },
                { label: 'City', value: terminalState.data?.address?.city || (terminalState.isLoading && <Loading isLoading={true} />) },
                { label: 'State/Province', value: terminalState.data?.address?.stateProvince ? `${usaStates.find((state) => state.value === terminalState.data?.address?.stateProvince)?.label} (${terminalState.data?.address?.stateProvince})` : (terminalState.isLoading && <Loading isLoading={true} />) },
                { label: 'Zip/Postal Code', value: terminalState.data?.address?.zipPostalCode || (terminalState.isLoading && <Loading isLoading={true} />) },
                { label: 'Country', value: terminalState.data?.address?.country ? `${countryOptions.find((country) => country.value === terminalState.data?.address?.country)?.label}` : (terminalState.isLoading && <Loading isLoading={true} />) },
                ],

                secondaryDetails: terminalState.data?.address?.isMailingAddressSame ? [] : [
                    { label: 'Mailing Address', value: terminalState.data?.address?.mailingAddress || (terminalState.isLoading && <Loading isLoading={true} />) },
                    { label: 'City', value: terminalState.data?.address?.mailingCity || (terminalState.isLoading && <Loading isLoading={true} />) },
                    { label: 'State/Province', value: terminalState.data?.address?.mailingStateProvince ? `${usaStates.find((state) => state.value === terminalState.data?.address?.mailingStateProvince)?.label} (${terminalState.data?.address?.mailingStateProvince})` : (terminalState.isLoading && <Loading isLoading={true} />) },
                    { label: 'Zip/Postal Code', value: terminalState.data?.address?.mailingZipPostalCode || (terminalState.isLoading && <Loading isLoading={true} />) },
                    { label: 'Country', value: terminalState.data?.address?.mailingCountry ? `${countryOptions.find((country) => country.value === terminalState.data?.address?.mailingCountry)?.label}` : (terminalState.isLoading && <Loading isLoading={true} />) },
                ],
            },
            sectionTitles: {
                secondaryTitle: "Mailing Address",
            },
            editComponent: ({ onClose }: { onClose: () => void }) => <EditAddress onClose={onClose} />
        },
        {
            title: "Contact Information",
            details: {
                primaryDetails: [{ label: 'Phone Number', value: terminalState.data?.contact?.phoneNumber?.formatPhone() || (terminalState.isLoading && <Loading isLoading={true} />) }, { label: 'Terminal Email Distribution List', value: terminalState.data?.contact?.terminalEmail || (terminalState.isLoading && <Loading isLoading={true} />) }],
                secondaryDetails: [
                    { label: 'Manager Name', value: terminalState.data?.contact?.managerName || (terminalState.isLoading && <Loading isLoading={true} />) },
                    { label: 'Manager Phone', value: terminalState.data?.contact?.managerPhone?.formatPhone() || (terminalState.isLoading && <Loading isLoading={true} />) },
                    { label: 'Manager Email', value: terminalState.data?.contact?.managerEmail || (terminalState.isLoading && <Loading isLoading={true} />) },
                ],
                tertiaryDetails:  terminalState.data?.terminalType?.type === "Agent Terminal" ?
                 [
                    { label: 'Agent Name', value: terminalState.data?.contact?.agentName || (terminalState.isLoading && <Loading isLoading={true} />) },
                    { label: 'Agent Email', value: terminalState.data?.contact?.agentEmail || (terminalState.isLoading && <Loading isLoading={true} />) },
                    { label: 'Agent Phone', value: terminalState.data?.contact?.agentPhone?.formatPhone() || (terminalState.isLoading && <Loading isLoading={true} />) },
                    { label: 'Agent Address', value: terminalState.data?.contact?.agentAddress || (terminalState.isLoading && <Loading isLoading={true} />) },
                    { label: 'City', value: terminalState.data?.contact?.agCity || (terminalState.isLoading && <Loading isLoading={true} />) },
                    { label: 'State', value: terminalState.data?.contact?.agState ? `${usaStates.find((state) => state.value === terminalState.data?.contact?.agState)?.label} (${terminalState.data?.contact?.agState})` : (terminalState.isLoading && <Loading isLoading={true} />) },
                ] : [],
            },
            sectionTitles: {
                secondaryTitle: "Manager Details",
                tertiaryTitle: "Agent Details",
            },
            editComponent: ({ onClose }: { onClose: () => void }) => <EditContact onClose={onClose} />
        },
        {
            title: "Region Information",
            details: {
                primaryDetails: [{ label: 'Region', value: terminalState.data?.region?.regionName || (terminalState.isLoading && <Loading isLoading={true} />) }],
                secondaryDetails: [
                    { label: 'Vice President Name', value: terminalState.data?.region?.vpName || (terminalState.isLoading && <Loading isLoading={true} />) },
                    { label: 'Vice President Phone', value: terminalState.data?.region?.vpPhone?.formatPhone() || (terminalState.isLoading && <Loading isLoading={true} />) },
                    { label: 'Vice President Email', value: terminalState.data?.region?.vpEmail || (terminalState.isLoading && <Loading isLoading={true} />) },
                ],
                tertiaryDetails: [
                    { label: 'Director Name', value: terminalState.data?.region?.directorName || (terminalState.isLoading && <Loading isLoading={true} />) },
                    { label: 'Director Phone', value: terminalState.data?.region?.directorPhone?.formatPhone() || (terminalState.isLoading && <Loading isLoading={true} />) },
                    { label: 'Director Email', value: terminalState.data?.region?.directorEmail || (terminalState.isLoading && <Loading isLoading={true} />) },
                ]
            },
            sectionTitles: {
                secondaryTitle: "Vice President Details",
                tertiaryTitle: "Director Details"
            },
            editComponent: ({ onClose }: { onClose: () => void }) => <EditRegion onClose={onClose} />
        },
    ];

    return <>
        {sections.map(({ title, details, secondaryInfo, sectionTitles, editComponent }, index) => (
            <BoxSection
                key={index}
                title={title}
                details={details}
                secondaryInfo={secondaryInfo}
                sectionTitles={sectionTitles}
                editComponent={editComponent}
            />
        ))}
    </>;
};

export default DetailsWidget;
